<template>
  <div class="container__wrapper">
    <topbar>
      <div class="topbar__panel">
        <div class="topbar__heading">
          <h1>Settings</h1>
          <Breadcrumb />
        </div>
      </div>

      <TopbarPanel>
        <div class="navlinks__item">
          <InertiaLink
            :href="route('settings.application.index')"
            :class="{ 'navlinks__link--active': isUrl(`settings/application`) }"
            class="navlinks__link"
          >
            Application
          </InertiaLink>
        </div>
        <div class="navlinks__item">
          <InertiaLink
            :href="route('settings.users.index')"
            :class="{ 'navlinks__link--active': isUrl(`settings/users`) }"
            class="navlinks__link"
          >
            Users
          </InertiaLink>
        </div>
      </TopbarPanel>
    </topbar>

    <sidebar />

    <ValidationObserver
      v-slot="{ invalid }"
      ref="form"
      tag="form"
      class="form"
      method="POST"
      @submit.prevent="save"
    >
      <div class="content content--boxed">
        <div class="content__wrapper">
          <div class="content__panel content__panel--v">
            <div class="content__block">
              <avatar v-model="form.avatar" />

              <div class="row">
                <div class="col col--4">
                  <div class="content__heading">
                    <div class="data-group data-group--v">
                      <h4>Account Information</h4>
                      <p class="data data--muted">Please provide your personal information by filling up the appropriate forms. Take note of the required fields marked with red icon.</p>
                    </div>
                  </div>
                </div>

                <div class="col col--8">
                  <div class="row">
                    <div class="col col--12">
                      <FormText
                        v-model="form.name"
                        :rules="['required']"
                        name="name"
                        label="Display Name"
                      />
                    </div>
                  </div>

                  <div class="row">
                    <div class="col col--12">
                      <FormText
                        v-model="form.username"
                        :rules="['required']"
                        name="username"
                      />
                    </div>
                  </div>

                  <div class="row">
                    <div class="col col--12">
                      <FormText
                        v-model="form.email"
                        :rules="['required', 'email']"
                        name="email"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="content__panel content__panel--v">
            <div class="row">
              <div class="col col--4">
                <div class="content__heading">
                  <div class="data-group data-group--v">
                    <h4>Password</h4>
                    <p class="data data--muted">If a user forgets the password for their managed account, or if you think their account has been compromised, you can reset their password.</p>
                  </div>
                </div>
              </div>
              <div class="col col--8">
                <div class="row">
                  <div class="col col--12">
                    <FormPassword
                      v-model="form.password"
                      :rules="['required', 'min:6', 'max:50']"
                      name="password"
                    />
                  </div>
                </div>

                <div class="row">
                  <div class="col col--12">
                    <FormPassword
                      v-model="form.password_confirmation"
                      :rules="['required', 'min:6', 'max:50']"
                      name="password_confirmation"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="content__panel content__panel--v">
            <div class="row">
              <div class="col col--4">
                <div class="content__heading">
                  <div class="data-group data-group--v">
                    <h4>Access Groups</h4>
                    <p class="data data--muted">Add user to an existing group or create a new one. Using groups is a best way to manage user's permissions by job functions.</p>
                  </div>
                </div>
              </div>

              <div class="col col--8">
                <div
                  v-for="role in roles"
                  :key="role.id"
                  class="row"
                >
                  <div class="col col--12">
                    <div class="form-check-list form-check-list--h">
                      <div class="form-check u-br">
                        <input
                          :id="`role-${role.name}`"
                          v-model="form.roles"
                          :value="role.name"
                          type="checkbox"
                          class="form-check__input"
                        >
                        <label
                          :for="`role-${role.name}`"
                          class="form-check__label"
                        >
                          <div class="data-group data-group--v">
                            <span class="data data--highlight">{{ role.name }}</span>
                            <span class="data data--muted">{{ role.description }}</span>
                          </div>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="content__panel content__panel--v">
            <div class="row">
              <ButtonLoader
                :disabled="invalid"
                :loading="loading"
                class="u-pos-r"
              >
                Save Changes
              </ButtonLoader>
            </div>
          </div>
        </div>
      </div>
    </ValidationObserver>
  </div>
</template>

<script>
import Topbar from '@components/Topbar'
import Sidebar from '@components/Sidebar'
import FormText from '@components/forms/FormText'
import Avatar from '@components/shared/Avatar'
import ButtonLoader from '@components/shared/ButtonLoader'
import Dashboard from '@layouts/Dashboard'
import TopbarPanel from '@components/shared/TopbarPanel'
import Breadcrumb from '@components/shared/Breadcrumb'
import { ValidationObserver } from 'vee-validate'
import FormPassword from '@components/forms/FormPassword'

export default {
  metaInfo () {
    return {
      title: 'Settings | Users',
    }
  },
  components: {
    FormPassword,
    Breadcrumb,
    ValidationObserver,
    TopbarPanel,
    ButtonLoader,
    Avatar,
    Topbar,
    FormText,
    Sidebar,
  },
  layout: Dashboard,
  props: {
    errors: Object,
    roles: {
      type: Array,
      required: true,
    },
  },
  data () {
    return {
      form: this.$inertia.form({
        roles: [],
        name: null,
        email: null,
        username: null,
        avatar: null,
        password: null,
        password_confirmation: null,
      }),
      loading: false,
    }
  },
  mounted () {
    this.$store.dispatch('breadcrumb/init', [
      { label: 'Settings', url: '/settings/users' },
      { label: 'New User' },
    ])
  },
  methods: {
    save () {
      this.form.post(this.route('settings.users.store'), {
        onStart: () => this.loading = true,
        onFinish: () => this.loading = false,
      })
    },
  },
}
</script>

